import { z } from 'zod';
import { TextSlot, useTextProp } from '@docgen/sdk';
import type { BlockDef } from '../types.js';

/**
 * The button link comes from editable document data, so only http(s) and mailto
 * schemes may render as an anchor — a `javascript:`/`data:` href is dropped to a
 * plain button, so a document can't smuggle in an XSS payload.
 */
function safeHref(url: unknown): string | undefined {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed === '') return undefined;
  try {
    const scheme = new URL(trimmed, 'https://x.invalid').protocol;
    if (scheme === 'http:' || scheme === 'https:' || scheme === 'mailto:') return trimmed;
  } catch {
    // not a parseable URL — fall through
  }
  return undefined;
}

export const ctaBandBlock: BlockDef = {
  type: 'ctaBand',
  name: 'CTA band',
  description: 'Dark call-to-action panel with button and signature.',
  category: 'brand',
  icon: 'megaphone',
  propsSchema: z.object({
    heading: z.string(),
    body: z.string(),
    buttonLabel: z.string(),
    /** When set, the button becomes a link (e.g. a mailto: to the salesperson). */
    buttonHref: z.string().default(''),
    sigHtml: z.string().default(''),
  }),
  propFields: [
    { kind: 'text', name: 'heading', label: 'Heading', default: 'Ready when you are' },
    { kind: 'text', name: 'body', label: 'Body', default: 'Say the word and we will hold this date.' },
    { kind: 'text', name: 'buttonLabel', label: 'Button label', default: 'Confirm this date' },
    { kind: 'text', name: 'buttonHref', label: 'Button link (URL or mailto:)' },
    { kind: 'richtext', name: 'sigHtml', label: 'Signature' },
  ],
  styleFields: [],
  defaults: {
    props: {
      heading: 'Ready when you are',
      body: 'Say the word and we will hold this date.',
      buttonLabel: 'Confirm this date',
      buttonHref: '',
      sigHtml: '',
    },
  },
  Component: function CtaBand({ attrs, style }) {
    const sig = useTextProp('sigHtml');
    const href = safeHref(useTextProp('buttonHref'));
    return (
      <div {...attrs} className="cta" style={style}>
        <TextSlot name="heading" as="h2" />
        <TextSlot name="body" as="p" />
        {href ? (
          <a href={href} className="btn">
            <TextSlot name="buttonLabel" as="span" />
          </a>
        ) : (
          <TextSlot name="buttonLabel" as="span" className="btn" />
        )}
        {sig ? <p className="who" dangerouslySetInnerHTML={{ __html: sig }} /> : null}
      </div>
    );
  },
};

export const calloutBlock: BlockDef = {
  type: 'callout',
  name: 'Callout',
  description: 'Highlight panel — accent (yellow-edged) or quiet (soft mist).',
  category: 'brand',
  icon: 'sparkles',
  propsSchema: z.object({
    variant: z.enum(['accent', 'quiet']).default('accent'),
    big: z.string().default(''),
    text: z.string(),
  }),
  propFields: [
    { kind: 'select', name: 'variant', label: 'Variant', options: ['accent', 'quiet'], default: 'accent' },
    { kind: 'text', name: 'big', label: 'Headline', default: 'Highlight' },
    { kind: 'text', name: 'text', label: 'Text', default: 'Supporting detail.' },
  ],
  styleFields: [],
  defaults: { props: { variant: 'accent', big: 'Highlight', text: 'Supporting detail.' } },
  Component: function Callout({ attrs, style }) {
    const quiet = useTextProp('variant') === 'quiet';
    const big = useTextProp('big');
    return (
      <div {...attrs} className={quiet ? 'mission' : 'savings'} style={style}>
        {big || !quiet ? <TextSlot name="big" as="div" className="big" /> : null}
        <TextSlot name="text" as="p" />
      </div>
    );
  },
};

export const pageFooterBlock: BlockDef = {
  type: 'pageFooter',
  name: 'Page footer',
  description: 'Thin bordered footer line with left and right text.',
  category: 'brand',
  icon: 'panel-bottom-dashed',
  propsSchema: z.object({ left: z.string(), right: z.string() }),
  propFields: [
    { kind: 'text', name: 'left', label: 'Left text', default: 'Best Corporate Events & TeamBuilding®' },
    { kind: 'text', name: 'right', label: 'Right text', default: '' },
  ],
  styleFields: [],
  defaults: {
    props: { left: 'Best Corporate Events & TeamBuilding®', right: 'Prepared for you' },
  },
  Component: function PageFooter({ attrs, style }) {
    return (
      <div {...attrs} className="foot" style={style}>
        <TextSlot name="left" as="span" />
        <TextSlot name="right" as="span" />
      </div>
    );
  },
};

export const coverDecorBlock: BlockDef = {
  type: 'coverDecor',
  name: 'Cover decor',
  description: 'Geometric orb background; content flows above it.',
  category: 'brand',
  icon: 'circle-dashed',
  propsSchema: z.object({}),
  propFields: [],
  styleFields: ['pad', 'gap', 'justify'],
  defaults: { props: {}, children: [] },
  acceptsChildren: true,
  Component: function CoverDecor({ attrs, style, children }) {
    return (
      <div {...attrs} className="decor" style={style}>
        <div className="orb orb--a" />
        <div className="orb orb--b" />
        <div className="orb orb--c" />
        <div className="decor-content" style={{ gap: style.gap, justifyContent: style.justifyContent }}>
          {children}
        </div>
      </div>
    );
  },
};
